import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Shield, Mail, UserCheck } from 'lucide-react'
import { auth } from '../../lib/supabase'
import { createFieldSurveyorSoloSignup } from '../../lib/services/billing'

type Branch = 'choose' | 'invited' | 'solo'

export function FieldSurveyorSignupPage() {
  const navigate = useNavigate()
  const [branch, setBranch] = useState<Branch>('choose')
  const [form, setForm] = useState({
    email: '',
    password: '',
    confirm_password: '',
    first_name: '',
    last_name: '',
    facility_name: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  async function handleSoloSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (form.password !== form.confirm_password) {
      setError('Passwords do not match')
      return
    }
    setIsSubmitting(true)
    setError(null)
    try {
      const signup = await createFieldSurveyorSoloSignup({
        email: form.email.trim(),
        password: form.password,
        first_name: form.first_name.trim() || undefined,
        last_name: form.last_name.trim() || undefined,
        facility_name: form.facility_name.trim(),
      })
      const { error: signInError } = await auth.signInWithPassword({
        email: signup.email,
        password: form.password,
      })
      if (signInError) throw signInError
      navigate(signup.next_path, { replace: true })
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#f6fbff,_#e9eff6_52%,_#e3ebf3)] px-4 py-8">
      <div className="mx-auto max-w-md">
        <div className="v11-section overflow-hidden">
          <div className="border-b border-slate-200 bg-white px-6 py-5">
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-[var(--brand-primary)] p-1.5 text-white">
                <Shield size={16} />
              </div>
              <span className="font-brand text-xl font-bold text-[var(--brand-deep)]">ASC Survey</span>
            </div>
            <h1 className="mt-4 font-brand text-2xl font-bold text-[var(--brand-deep)]">
              Field Surveyor Access
            </h1>
            <p className="mt-1 text-sm text-slate-600">Get started as an independent compliance surveyor.</p>
          </div>

          <div className="px-6 py-6">
            {error && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {branch === 'choose' && (
              <div className="space-y-3">
                <p className="text-sm font-semibold text-slate-700">How are you joining?</p>
                <button
                  onClick={() => setBranch('invited')}
                  className="flex w-full items-start gap-4 rounded-xl border-2 border-slate-200 p-4 text-left hover:border-blue-400 hover:bg-blue-50"
                >
                  <Mail size={22} className="mt-0.5 shrink-0 text-blue-500" />
                  <div>
                    <p className="font-bold text-slate-800">I was invited</p>
                    <p className="mt-0.5 text-sm text-slate-500">A consultant or enterprise admin sent me an invitation link.</p>
                  </div>
                </button>
                <button
                  onClick={() => setBranch('solo')}
                  className="flex w-full items-start gap-4 rounded-xl border-2 border-slate-200 p-4 text-left hover:border-blue-400 hover:bg-blue-50"
                >
                  <UserCheck size={22} className="mt-0.5 shrink-0 text-blue-500" />
                  <div>
                    <p className="font-bold text-slate-800">I work independently</p>
                    <p className="mt-0.5 text-sm text-slate-500">Independent compliance consultant — create my own account.</p>
                  </div>
                </button>
                <p className="pt-2 text-center text-xs text-slate-400">
                  Already have an account?{' '}
                  <Link to="/login" className="text-blue-600 hover:underline">Sign in</Link>
                </p>
              </div>
            )}

            {branch === 'invited' && (
              <div className="space-y-4">
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
                  <p className="font-bold">Check your email</p>
                  <p className="mt-1">
                    Your invitation includes a magic link. Click it to create your account and get connected to your assigned facilities.
                  </p>
                  <p className="mt-2 text-xs text-blue-600">
                    No email? Ask your admin to resend the invite via User Management.
                  </p>
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => setBranch('choose')}
                    className="flex-1 rounded-lg border border-slate-200 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                  >
                    Back
                  </button>
                  <Link
                    to="/login"
                    className="flex-1 rounded-lg bg-blue-600 py-2 text-center text-sm font-bold text-white hover:bg-blue-700"
                  >
                    Go to sign in
                  </Link>
                </div>
              </div>
            )}

            {branch === 'solo' && (
              <form onSubmit={handleSoloSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-bold text-slate-600">First name</label>
                    <input name="first_name" value={form.first_name} onChange={handleChange}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Optional" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-bold text-slate-600">Last name</label>
                    <input name="last_name" value={form.last_name} onChange={handleChange}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Optional" />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-bold text-slate-600">Email *</label>
                  <input name="email" type="email" required value={form.email} onChange={handleChange}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="you@example.com" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-bold text-slate-600">Primary facility name *</label>
                  <input name="facility_name" required value={form.facility_name} onChange={handleChange}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Riverside Surgery Center" />
                  <p className="mt-0.5 text-xs text-slate-400">You can add more facilities after signup.</p>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-bold text-slate-600">Password *</label>
                  <input name="password" type="password" required minLength={8} value={form.password} onChange={handleChange}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Minimum 8 characters" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-bold text-slate-600">Confirm password *</label>
                  <input name="confirm_password" type="password" required value={form.confirm_password} onChange={handleChange}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Repeat password" />
                </div>
                <div className="flex gap-2 pt-1">
                  <button type="button" onClick={() => setBranch('choose')}
                    className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                    Back
                  </button>
                  <button type="submit" disabled={isSubmitting}
                    className="flex-1 rounded-lg bg-blue-600 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50">
                    {isSubmitting ? 'Creating…' : 'Create account'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
